/**
 * AiPromptsPanel — view / edit / reset the system prompts guiding every AI.
 *
 * View-only by default; an explicit "Edit" toggle unlocks the textarea (so the
 * tuned prompts can't be changed by accident). "Reset to default" clears the
 * override. A sample preview renders the exact system + user message the AI
 * receives — for the BCP report, the admin can pick a sector/region/segment to
 * see how the prompt adapts.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2, Pencil, Save, RotateCcw, X, Eye, ShieldAlert, CheckCircle2,
} from "lucide-react";

const SAMPLE_MODES = [
  { value: "solopreneur", label: "Solopreneur" },
  { value: "sme", label: "SME" },
  { value: "corporate", label: "Corporate dept." },
];

export default function AiPromptsPanel() {
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/ai-prompts");
      setPrompts(data.prompts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading || !prompts) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-6" data-testid="ai-prompts-panel">
      <div className="rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900 dark:text-amber-100">
          <p className="font-semibold">These prompts steer the AI on every request</p>
          <p className="mt-1 text-amber-900/80 dark:text-amber-100/80">
            View by default — toggle <strong>Edit</strong> to change one. Your edits take effect on the
            next generation (no restart). <strong>Reset to default</strong> restores the tuned baseline at any time.
          </p>
        </div>
      </div>

      {prompts.map((p) => (
        <PromptCard key={p.key} prompt={p} onChanged={reload} />
      ))}
    </div>
  );
}

function PromptCard({ prompt, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(prompt.effective);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sampleMode, setSampleMode] = useState("sme");
  const [sampleIndustry, setSampleIndustry] = useState("");
  const [sampleCountry, setSampleCountry] = useState("");

  useEffect(() => { setDraft(prompt.effective); }, [prompt.effective]);

  const save = async () => {
    if (!draft.trim()) { toast.error("Prompt cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/ai-prompts/${prompt.key}`, { content: draft });
      toast.success(`${prompt.label} updated.`);
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${prompt.label}" to the default prompt? Your override will be removed.`)) return;
    setResetting(true);
    try {
      await api.delete(`/admin/ai-prompts/${prompt.key}`);
      toast.success(`${prompt.label} reset to default.`);
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const body = prompt.kind === "report"
        ? { mode: sampleMode, industry: sampleIndustry, country: sampleCountry }
        : {};
      const { data } = await api.post(`/admin/ai-prompts/${prompt.key}/preview`, body);
      setPreview(data);
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`prompt-card-${prompt.key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">{prompt.label}</h3>
            {prompt.has_override ? (
              <span className="px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-500/15 text-teal-800 dark:text-teal-300 text-[10px] font-medium uppercase tracking-wide">Customised</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-medium uppercase tracking-wide">Default</span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{prompt.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              data-testid={`prompt-edit-${prompt.key}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          ) : (
            <>
              <button
                onClick={() => { setEditing(false); setDraft(prompt.effective); }}
                data-testid={`prompt-cancel-${prompt.key}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid={`prompt-save-${prompt.key}`}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </>
          )}
          {prompt.has_override && (
            <button
              onClick={reset}
              disabled={resetting}
              data-testid={`prompt-reset-${prompt.key}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-amber-300 dark:border-amber-500/40 text-amber-700 dark:text-amber-300 text-sm font-medium hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-50 transition-colors"
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reset
            </button>
          )}
        </div>
      </div>

      <textarea
        value={editing ? draft : prompt.effective}
        onChange={(e) => setDraft(e.target.value)}
        readOnly={!editing}
        data-testid={`prompt-textarea-${prompt.key}`}
        className={`mt-4 w-full h-56 px-3 py-2 rounded-md border text-[13px] font-mono leading-relaxed focus:outline-none transition-colors ${
          editing
            ? "border-teal-500 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-teal-500/20"
            : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 text-slate-600 dark:text-slate-300 cursor-default"
        }`}
      />

      {/* Preview */}
      <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex items-end gap-3 flex-wrap">
          {prompt.kind === "report" && (
            <>
              <label className="text-xs text-slate-500 dark:text-slate-400">
                Segment
                <select
                  value={sampleMode}
                  onChange={(e) => setSampleMode(e.target.value)}
                  data-testid={`preview-mode-${prompt.key}`}
                  className="mt-1 block h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm text-slate-800 dark:text-slate-100"
                >
                  {SAMPLE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-500 dark:text-slate-400">
                Sector
                <input
                  value={sampleIndustry}
                  onChange={(e) => setSampleIndustry(e.target.value)}
                  placeholder="e.g. hospitality"
                  data-testid={`preview-industry-${prompt.key}`}
                  className="mt-1 block h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm w-36"
                />
              </label>
              <label className="text-xs text-slate-500 dark:text-slate-400">
                Country
                <input
                  value={sampleCountry}
                  onChange={(e) => setSampleCountry(e.target.value)}
                  placeholder="e.g. Jamaica"
                  data-testid={`preview-country-${prompt.key}`}
                  className="mt-1 block h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm w-36"
                />
              </label>
            </>
          )}
          <button
            onClick={runPreview}
            disabled={previewing}
            data-testid={`prompt-preview-${prompt.key}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 disabled:opacity-50 transition-colors"
          >
            {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Preview what the AI receives
          </button>
        </div>

        {showPreview && preview && (
          <div className="mt-4 space-y-3" data-testid={`preview-output-${prompt.key}`}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1">System message (your prompt)</p>
              <pre className="whitespace-pre-wrap text-[12px] text-slate-700 dark:text-slate-200 font-mono max-h-52 overflow-y-auto bg-slate-50 dark:bg-slate-950 rounded p-3 border border-slate-100 dark:border-slate-800">{preview.system}</pre>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1">User message (sample data the AI works on)</p>
              <pre className="whitespace-pre-wrap text-[12px] text-slate-700 dark:text-slate-200 font-mono max-h-64 overflow-y-auto bg-slate-50 dark:bg-slate-950 rounded p-3 border border-slate-100 dark:border-slate-800">{preview.user}</pre>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              To see live output for these settings, save your prompt then use the Generate buttons (Marketing) or run a scan in this sector.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
